import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  PaperPlaneTiltIcon,
  CheckIcon,
  ChecksIcon,
  DotsThreeVerticalIcon,
  ProhibitIcon,
  WarningIcon,
  ChatTeardropDotsIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const WS_URL = (() => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const url = new URL(base);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = "/api/ws/dm";
  return url.toString();
})();

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function ThreadRow({ thread, active, onClick }) {
  const u = thread.other_user || {};
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3 border-b border-zinc-100 text-left transition-colors ${
        active ? "bg-[#1D78AB]/5" : "hover:bg-zinc-50"
      }`}
      data-testid={`dm-thread-${thread.id}`}
    >
      <div className="relative h-11 w-11 rounded-full bg-zinc-100 overflow-hidden flex-shrink-0">
        {u.picture ? (
          <img src={u.picture} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full grid place-items-center text-zinc-400 font-mono text-xs">
            @
          </div>
        )}
        {thread.unread && (
          <span className="absolute top-0 right-0 h-3 w-3 rounded-full bg-[#1D78AB] border-2 border-white" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="font-bold text-sm truncate">@{u.username}</div>
          <div className="text-[11px] text-zinc-500 flex-shrink-0">
            {timeAgo(thread.last_message_at || thread.created_at)}
          </div>
        </div>
        <div
          className={`text-[12px] truncate ${
            thread.unread ? "text-zinc-900 font-semibold" : "text-zinc-500"
          }`}
        >
          {thread.last_message_text || "New conversation"}
        </div>
      </div>
    </button>
  );
}

function MessageBubble({ msg, isMine, otherUser, onDelete }) {
  const isDeleted = !!msg.deleted_at || msg.text === "[deleted]";
  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"} mb-2 group`}>
      {/* Trash icon for own messages — appears on hover/focus only.
          Hides when the message is already soft-deleted. */}
      {isMine && !isDeleted && (
        <button
          type="button"
          onClick={() => onDelete(msg)}
          className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity h-7 w-7 grid place-items-center rounded-full text-zinc-400 hover:text-rose-600 hover:bg-rose-50 self-end mb-0.5 mr-1"
          aria-label="Delete message"
          data-testid={`dm-delete-${msg.id}`}
          title="Delete message"
        >
          <TrashIcon size={13} />
        </button>
      )}
      <div
        className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-[14px] leading-snug whitespace-pre-wrap break-words ${
          isDeleted
            ? "bg-zinc-100 text-zinc-400 italic rounded-br-sm border border-zinc-200"
            : isMine
            ? "bg-[#1D78AB] text-white rounded-br-sm"
            : "bg-zinc-100 text-zinc-900 rounded-bl-sm"
        }`}
        data-testid={`dm-msg-${msg.id}`}
      >
        {isDeleted ? "Message deleted" : msg.text}
        {isMine && !isDeleted && (
          <span className="ml-1.5 inline-flex items-center align-middle text-[11px] opacity-80">
            {msg.read_at ? <ChecksIcon size={12} /> : <CheckIcon size={12} />}
          </span>
        )}
      </div>
    </div>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [showBlock, setShowBlock] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportText, setReportText] = useState("");
  const wsRef = useRef(null);
  const scrollRef = useRef(null);

  const activeThread = useMemo(
    () => threads.find((t) => t.id === activeId) || null,
    [threads, activeId]
  );

  // ---- Initial load + tab routing ---------------------------------
  useEffect(() => {
    refreshThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const tid = params.get("thread_id");
    if (tid && tid !== activeId) setActiveId(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // ---- WS connection ---------------------------------------------
  useEffect(() => {
    if (!user) return;
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "dm.new") {
          // If the message is for the active thread, append it; otherwise
          // bump the thread list.
          if (data.thread_id === activeId) {
            setMessages((prev) => {
              if (prev.some((m) => m.id === data.message.id)) return prev;
              return [...prev, data.message];
            });
            // Mark as read since it's currently open.
            api.post(`/dm/threads/${data.thread_id}/read`).catch(() => {});
          }
          refreshThreads();
        } else if (data.type === "dm.read" && data.thread_id === activeId) {
          setMessages((prev) =>
            prev.map((m) =>
              m.recipient_id === data.reader_id && !m.read_at
                ? { ...m, read_at: data.at }
                : m
            )
          );
        } else if (data.type === "dm.delete" && data.thread_id === activeId) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === data.message_id
                ? { ...m, text: "[deleted]", deleted_at: data.at }
                : m
            )
          );
          refreshThreads();
        }
      } catch {
        /* ignore non-JSON pings */
      }
    };
    return () => {
      try {
        ws.close();
      } catch {
        /* noop */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeId]);

  // ---- Active-thread message load + read receipt ----------------
  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }
    setLoadingMsgs(true);
    api
      .get(`/dm/threads/${activeId}/messages`)
      .then((r) => {
        setMessages(r.data.items || []);
        return api.post(`/dm/threads/${activeId}/read`);
      })
      .then(() => refreshThreads())
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoadingMsgs(false));
    // Sync the URL so the page is shareable / back-buttonable.
    const next = new URLSearchParams(params);
    next.set("thread_id", activeId);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // Auto-scroll to the latest message.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const refreshThreads = async () => {
    try {
      const { data } = await api.get("/dm/threads");
      setThreads(data.items || []);
    } catch {
      /* noop — UI keeps the stale list */
    }
  };

  const send = async () => {
    if (!activeId) return;
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/dm/threads/${activeId}/messages`, {
        text,
      });
      setMessages((prev) => [...prev, data]);
      setDraft("");
      refreshThreads();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  const deleteMsg = async (msg) => {
    if (!msg?.id) return;
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    // Optimistic update — flip bubble to "Message deleted" before the
    // round-trip. WS broadcast will fire on the recipient too.
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msg.id
          ? { ...m, text: "[deleted]", deleted_at: new Date().toISOString() }
          : m
      )
    );
    try {
      await api.delete(`/dm/messages/${msg.id}`);
      refreshThreads();
    } catch (e) {
      // Revert on failure.
      setMessages((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, text: msg.text, deleted_at: null } : m))
      );
      toast.error(apiError(e));
    }
  };

  const blockNow = async () => {
    if (!activeThread) return;
    try {
      await api.post(`/users/${activeThread.other_user.user_id}/block`);
      toast.success(`@${activeThread.other_user.username} blocked.`);
      setShowBlock(false);
      navigate("/messages");
      setActiveId(null);
      refreshThreads();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const reportNow = async () => {
    if (!activeThread || reportText.trim().length < 3) return;
    try {
      await api.post(`/users/${activeThread.other_user.user_id}/report`, {
        reason: reportText.trim(),
      });
      toast.success("Report submitted. Our team will review it shortly.");
      setShowReport(false);
      setReportText("");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div
      className="min-h-screen bg-zinc-50 flex flex-col"
      data-testid="messages-page"
    >
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-3 sm:px-6 py-6">
        <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden flex h-[calc(100vh-160px)] min-h-[480px]">
          {/* Thread list */}
          <aside
            className={`w-full sm:w-80 border-r border-zinc-200 flex flex-col ${
              activeId ? "hidden sm:flex" : "flex"
            }`}
          >
            <div className="px-4 py-4 border-b border-zinc-200">
              <h1 className="font-display font-black text-xl tracking-tight">
                Messages
              </h1>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                Tap a profile then "Message" to start a new chat
              </p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {threads.length === 0 ? (
                <div className="p-6 text-sm text-zinc-500 text-center">
                  <ChatTeardropDotsIcon
                    size={32}
                    className="mx-auto text-zinc-300"
                  />
                  <p className="mt-2 font-semibold text-zinc-700">No messages yet</p>
                  <p className="mt-1 text-[12px]">
                    Visit a profile and tap "Message" to start a chat.
                  </p>
                </div>
              ) : (
                threads.map((t) => (
                  <ThreadRow
                    key={t.id}
                    thread={t}
                    active={t.id === activeId}
                    onClick={() => setActiveId(t.id)}
                  />
                ))
              )}
            </div>
          </aside>

          {/* Conversation */}
          <section
            className={`flex-1 flex flex-col ${
              activeId ? "flex" : "hidden sm:flex"
            }`}
          >
            {!activeThread ? (
              <div className="flex-1 grid place-items-center text-center p-10">
                <div>
                  <ChatTeardropDotsIcon
                    size={40}
                    className="mx-auto text-zinc-300"
                  />
                  <p className="mt-3 font-display font-bold text-lg text-zinc-700">
                    Select a conversation
                  </p>
                  <p className="text-sm text-zinc-500 mt-1">
                    Or start a new one from any profile.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <header className="px-4 py-3 border-b border-zinc-200 flex items-center gap-3">
                  <button
                    className="sm:hidden text-zinc-500 hover:text-zinc-900 px-1"
                    onClick={() => setActiveId(null)}
                    aria-label="Back"
                    data-testid="dm-back-btn"
                  >
                    ←
                  </button>
                  <Link
                    to={`/seller/${activeThread.other_user.user_id}`}
                    className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80"
                  >
                    <div className="h-10 w-10 rounded-full bg-zinc-100 overflow-hidden flex-shrink-0">
                      {activeThread.other_user.picture ? (
                        <img
                          src={activeThread.other_user.picture}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="h-full w-full grid place-items-center text-zinc-400 font-mono text-xs">
                          @
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-sm truncate">
                        @{activeThread.other_user.username}
                      </div>
                      <div className="text-[11px] text-zinc-500">
                        {activeThread.other_user.is_seller ? "Seller" : "Buyer"}
                        {activeThread.other_user.verified && " · Verified"}
                      </div>
                    </div>
                  </Link>
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      className="h-9 w-9 grid place-items-center rounded-full hover:bg-zinc-100"
                      aria-label="Conversation options"
                      data-testid="dm-options-btn"
                    >
                      <DotsThreeVerticalIcon size={18} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onSelect={() => setShowBlock(true)}
                        data-testid="dm-block-menu-item"
                      >
                        <ProhibitIcon size={16} className="mr-2" /> Block user
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => setShowReport(true)}
                        data-testid="dm-report-menu-item"
                      >
                        <WarningIcon size={16} className="mr-2" /> Report user
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </header>

                <div
                  ref={scrollRef}
                  className="flex-1 overflow-y-auto px-4 py-4 bg-zinc-50/40"
                  data-testid="dm-messages-scroll"
                >
                  {loadingMsgs ? (
                    <p className="text-sm text-zinc-500 text-center mt-10">
                      Loading…
                    </p>
                  ) : messages.length === 0 ? (
                    <p className="text-sm text-zinc-500 text-center mt-10">
                      Say hi to @{activeThread.other_user.username} 👋
                    </p>
                  ) : (
                    messages.map((m) => (
                      <MessageBubble
                        key={m.id}
                        msg={m}
                        isMine={m.sender_id === user?.user_id}
                        otherUser={activeThread.other_user}
                        onDelete={deleteMsg}
                      />
                    ))
                  )}
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    send();
                  }}
                  className="border-t border-zinc-200 p-3 flex items-end gap-2"
                  data-testid="dm-compose-form"
                >
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    placeholder={`Message @${activeThread.other_user.username}…`}
                    rows={1}
                    className="flex-1 resize-none rounded-2xl border border-zinc-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D78AB]/30 focus:border-[#1D78AB] max-h-32"
                    data-testid="dm-compose-input"
                  />
                  <button
                    type="submit"
                    disabled={!draft.trim() || sending}
                    className="h-10 w-10 grid place-items-center rounded-full bg-[#1D78AB] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#165d85]"
                    aria-label="Send"
                    data-testid="dm-send-btn"
                  >
                    <PaperPlaneTiltIcon size={18} weight="fill" />
                  </button>
                </form>
              </>
            )}
          </section>
        </div>
      </main>

      {/* Block confirmation */}
      <Dialog open={showBlock} onOpenChange={setShowBlock}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Block @{activeThread?.other_user.username}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600">
            They won't be able to message you, and you won't be able to message
            them. You'll also unfollow each other automatically.
          </p>
          <DialogFooter className="gap-2">
            <button
              onClick={() => setShowBlock(false)}
              className="px-4 py-2 rounded-full text-sm font-semibold border border-zinc-300 hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              onClick={blockNow}
              className="px-4 py-2 rounded-full text-sm font-bold bg-rose-600 text-white hover:bg-rose-700"
              data-testid="dm-block-confirm-btn"
            >
              Block
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report */}
      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Report @{activeThread?.other_user.username}</DialogTitle>
          </DialogHeader>
          <textarea
            value={reportText}
            onChange={(e) => setReportText(e.target.value)}
            placeholder="What happened? Be specific so our trust team can act."
            rows={4}
            className="w-full resize-none rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D78AB]/30 focus:border-[#1D78AB]"
            data-testid="dm-report-textarea"
          />
          <DialogFooter className="gap-2">
            <button
              onClick={() => setShowReport(false)}
              className="px-4 py-2 rounded-full text-sm font-semibold border border-zinc-300 hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              onClick={reportNow}
              disabled={reportText.trim().length < 3}
              className="px-4 py-2 rounded-full text-sm font-bold bg-[#1D78AB] text-white hover:bg-[#165d85] disabled:opacity-50"
              data-testid="dm-report-submit-btn"
            >
              Submit report
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
